// The contract every other module agrees on, ported from internal/core/types.go.
//
// This file is the browser's copy of the Go `core` package: geometry, cadence,
// identifiers and the two enums that travel on the wire (ClickKind, Verdict).
// Nothing here does I/O and nothing here imports anything else.
//
// It is a PORT, not an interpretation. Every constant below has a named
// counterpart in internal/core/types.go, and web/testdata/main.go emits the Go
// side of those numbers into a fixture the tests assert against, so the two
// copies cannot drift silently.

// --- board geometry -------------------------------------------------------
//
// 320x320 = 102,400 pixels. Chunks are 32x32, giving 10x10 = 100 chunks.

export const BoardW = 320;
export const BoardH = 320;
export const NumPixels = BoardW * BoardH;

export const ChunkSize = 32;
export const ChunksX = BoardW / ChunkSize;
export const ChunksY = BoardH / ChunkSize;
export const NumChunks = ChunksX * ChunksY;

/** PixelsPerChunk fits in a uint16 offset, which is what buys the u32 HP field. */
export const PixelsPerChunk = ChunkSize * ChunkSize;

// --- cadence --------------------------------------------------------------

export const TickIntervalMs = 500;
export const KeyframeEveryTicks = 20; // ~10s
export const AggregateEveryTick = 4; // ~2s
export const RegenEveryTicks = 120;
export const SnapshotEveryTicks = 10; // ~5s

// --- identifiers ----------------------------------------------------------
//
// These are documentation aliases, not nominal types: a PixelID is an index
// into a typed array and wrapping it in a branded type would cost a cast at
// every array access in the hot path for no defect this codebase has had.

/** A global index into the board, row-major: y*BoardW + x. */
export type PixelID = number;
/** A global chunk index, row-major: cy*ChunksX + cx. */
export type ChunkID = number;
/** An internal country id. 0 is Neutral. */
export type CountryID = number;

/**
 * TickNo counts ticks since the season began.
 *
 * On the wire it is a u64. Here it is a plain `number`, decoded through
 * `readTick` which refuses anything above Number.MAX_SAFE_INTEGER rather than
 * silently losing precision. At the 500 ms tick of `TickIntervalMs`, 2^53 ticks
 * is about 143 million years, so the guard is a decode error that will never
 * fire in play and never a wrong answer if it somehow does.
 */
export type TickNo = number;

export const NeutralCountry: CountryID = 0;

// --- board state ----------------------------------------------------------

/** Pixel flags, one byte, matching core.PixelFlags. */
export const FlagFortress = 1 << 0;
export const FlagCoastal = 1 << 1;
export const FlagBlockMember = 1 << 2;
export const FlagDisputed = 1 << 3;

export function isFortress(flags: number): boolean {
  return (flags & FlagFortress) !== 0;
}

// --- clicks ---------------------------------------------------------------

export const ClickAttack = 0;
export const ClickFortify = 1;
/** Ignores adjacency; legal only while the attacker's country holds zero pixels. */
export const ClickBeachhead = 2;

export type ClickKind = 0 | 1 | 2;

/** The highest defined click kind. A byte above it is a decode error. */
export const MaxClickKind = ClickBeachhead;

// --- verdicts -------------------------------------------------------------
//
// Three failure outcomes have to look different to the player (SPEC §3), which
// is why this enum is carried on SELF unmodified rather than collapsed into a
// boolean.

export const VerdictApplied = 0;
export const VerdictCaptured = 1;
export const VerdictIllegalAdjacency = 2;
export const VerdictNoBudget = 3;
export const VerdictLostRace = 4;
export const VerdictDropped = 5;
export const VerdictFrozen = 6;
export const VerdictNoCountry = 7;

export type Verdict = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7;

/**
 * The ceiling is the LAST verdict core defines, not a fixed number.
 * core.VerdictNoCountry was appended after the wire package shipped.
 */
export const MaxVerdict = VerdictNoCountry;

const VERDICT_NAMES = [
  'applied',
  'captured',
  'illegal-adjacency',
  'no-budget',
  'lost-race',
  'dropped',
  'frozen',
  'no-country',
] as const;

export function verdictName(v: number): string {
  return VERDICT_NAMES[v] ?? 'unknown';
}

/**
 * Refunds reports whether the click's budget is returned. Ported verbatim from
 * core.Verdict.Refunds: a click that never touched the board must never cost
 * the player anything.
 */
export function verdictRefunds(v: number): boolean {
  switch (v) {
    case VerdictIllegalAdjacency:
    case VerdictNoBudget:
    case VerdictDropped:
    case VerdictFrozen:
    case VerdictNoCountry:
      return true;
    default:
      return false;
  }
}

// --- geometry helpers -----------------------------------------------------

export function pixelX(p: PixelID): number {
  return p % BoardW;
}

export function pixelY(p: PixelID): number {
  return (p / BoardW) | 0;
}

export function at(x: number, y: number): PixelID {
  return y * BoardW + x;
}

export function inBounds(x: number, y: number): boolean {
  return x >= 0 && x < BoardW && y >= 0 && y < BoardH;
}

/** The chunk a pixel belongs to. */
export function chunkOf(p: PixelID): ChunkID {
  const cx = (p % BoardW) / ChunkSize | 0;
  const cy = ((p / BoardW) | 0) / ChunkSize | 0;
  return cy * ChunksX + cx;
}

/** The pixel's offset within its chunk — the value that goes on the wire. */
export function offsetOf(p: PixelID): number {
  const ox = (p % BoardW) % ChunkSize;
  const oy = ((p / BoardW) | 0) % ChunkSize;
  return oy * ChunkSize + ox;
}

/** The inverse of chunkOf/offsetOf. */
export function pixelOf(c: ChunkID, off: number): PixelID {
  const cx = c % ChunksX;
  const cy = (c / ChunksX) | 0;
  const ox = off % ChunkSize;
  const oy = (off / ChunkSize) | 0;
  return at(cx * ChunkSize + ox, cy * ChunkSize + oy);
}

/** The top-left pixel coordinate of a chunk. */
export function chunkOrigin(c: ChunkID): { x: number; y: number } {
  return { x: (c % ChunksX) * ChunkSize, y: ((c / ChunksX) | 0) * ChunkSize };
}

/**
 * The orthogonally adjacent pixels — 4-connectivity, matching core.Neighbours4.
 * Diagonals are excluded on purpose: 8-connectivity lets a front advance
 * through a corner touch and makes territory read as ragged.
 */
export function neighbours4(p: PixelID, out: number[]): number {
  const x = pixelX(p);
  const y = pixelY(p);
  let n = 0;
  const d = [
    [0, -1],
    [1, 0],
    [0, 1],
    [-1, 0],
  ] as const;
  for (const [dx, dy] of d) {
    const nx = x + dx;
    const ny = y + dy;
    if (inBounds(nx, ny)) {
      out[n++] = at(nx, ny);
    }
  }
  return n;
}

export function validPixel(p: number): boolean {
  return Number.isInteger(p) && p >= 0 && p < NumPixels;
}

export function validChunk(c: number): boolean {
  return Number.isInteger(c) && c >= 0 && c < NumChunks;
}

export function validOffset(o: number): boolean {
  return Number.isInteger(o) && o >= 0 && o < PixelsPerChunk;
}
